// CreateEngagementModal — the two-field "New engagement" create form, reached
// from the EngagementSwitcher dropdown footer (027-engagement-aggregate, Slice C,
// FR-010, User Story 2.3). Reuses the repo modal pattern (AddStepToVsmModal): a
// fixed `.ap-modal-backdrop` with click-outside, an inner `.ap-modal` that stops
// propagation, an autofocused first field, and `.ap-modal-cancel` /
// `.ap-modal-confirm` actions.
//
// CORAL DISCIPLINE: the primary "Create engagement" CTA is NEUTRAL/BLUE — only
// the first-run gateway may spend the reserved coral `--ap-accent`.

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import '../styles/CreateEngagementModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // Creates the engagement and switches to it; resolves when the create settles.
  onCreate: (input: { client: string; title: string; commandId: string }) => Promise<unknown>;
}

function newCommandId(): string {
  try {
    return `create-${crypto.randomUUID()}`;
  } catch {
    return `create-${Date.now().toString(36)}`;
  }
}

const CreateEngagementModal: React.FC<Props> = ({ isOpen, onClose, onCreate }) => {
  const [client, setClient] = useState('');
  const [title, setTitle] = useState('');
  const [commandId, setCommandId] = useState(newCommandId);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Reset the form each time the modal opens (the Client field autofocuses via
  // the autoFocus attribute on mount).
  useEffect(() => {
    if (!isOpen) return;
    setClient('');
    setTitle('');
    setCommandId(newCommandId());
    setError(null);
    setCreating(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (creating) return;
    const trimmed = client.trim();
    const trimmedTitle = title.trim();
    if (!trimmed || !trimmedTitle) {
      setError('Enter a client organisation and engagement title.');
      return;
    }
    setError(null);
    setCreating(true);
    try {
      const created = await onCreate({ client: trimmed, title: trimmedTitle, commandId });
      if (!created) {
        setError("We couldn't create the engagement. Your entries have not been lost. Try again.");
        return;
      }
      onClose();
    } catch {
      setError("We couldn't create the engagement. Your entries have not been lost. Try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="ap-modal-backdrop" onClick={creating ? undefined : onClose}>
      <div
        className="ap-modal create-eng-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Create engagement"
      >
        <div className="create-eng-modal-head">
          <h3>New engagement</h3>
          <button
            type="button"
            className="create-eng-modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={creating}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <form className="create-eng-modal-form" onSubmit={handleSubmit}>
          <p className="create-eng-modal-hint">
            Enter the client and engagement name. You will land on the Brief —
            narrate the engagement and the plan builds from it.
          </p>
          <label className="create-eng-modal-label" htmlFor="create-eng-client">
            Client organisation *
          </label>
          <input
            id="create-eng-client"
            className="create-eng-modal-input"
            value={client}
            onChange={(e) => setClient(e.target.value)}
            placeholder="e.g. Northwind Mutual"
            required
            autoFocus
            aria-label="Client"
          />

          <label className="create-eng-modal-label" htmlFor="create-eng-title">
            Engagement title *
          </label>
          <input
            id="create-eng-title"
            className="create-eng-modal-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Claims service modernisation"
            aria-label="Engagement title"
            required
          />
          <p className="create-eng-modal-hint">
            Use a name the client will recognise.
          </p>

          {error && (
            <div className="create-eng-modal-error" role="alert">
              {error}
            </div>
          )}

          <div className="create-eng-modal-actions">
            <button
              type="button"
              className="ap-modal-cancel"
              onClick={onClose}
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ap-modal-confirm create-eng-modal-confirm"
              disabled={creating}
              aria-label={creating ? 'Creating engagement' : 'Create engagement and start with the brief'}
            >
              {creating ? 'Creating engagement...' : 'Start with the brief'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEngagementModal;
